import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { CalendarIcon, Check, Copy, Link, Loader2, SendHorizontal } from 'lucide-react';
import { invokeFunction, supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { Product } from '@/types';
import { LinkType, sendMagicLinkEmail } from '@/utils/magic-link';
import { generateSubmissionIdentifier } from '@/utils/magic-link/helpers';

// Resend magic link (FIG-320 / REQ-038), moved from the Merchants list's Actions
// column to the merchant Settings tab. Behaviour is unchanged.

export interface MagicLinkMerchant {
  id: string;
  name: string;
  poc_name?: string | null;
  poc_email?: string | null;
  poc_phone?: string | null;
  ein?: string | null;
  products?: unknown;
  merchant_type?: string | null;
}

interface MagicLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  merchant: MagicLinkMerchant;
}

export const MagicLinkDialog: React.FC<MagicLinkDialogProps> = ({ open, onOpenChange, merchant }) => {
  const { toast } = useToast();
  const magicLinkDialogOpen = open;
  const setMagicLinkDialogOpen = onOpenChange;
  // Magic link states - store complete organization object to prevent changes from autofill
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [selectedOrgForLink, setSelectedOrgForLink] = useState<MagicLinkMerchant | null>(null);
  const [emailSent, setEmailSent] = useState(false);
  const [useAlternativeEmail, setUseAlternativeEmail] = useState(false);
  const [alternativeEmail, setAlternativeEmail] = useState('');
  const [debouncedAlternativeEmail, setDebouncedAlternativeEmail] = useState('');
  const [submissionIdentifier, setSubmissionIdentifier] = useState('');
  const [linkDestination, setLinkDestination] = useState<LinkType>('onboarding');
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const formatDateTimeLocal = (date: Date) => {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  // Convert a local datetime string (no timezone) to a UTC ISO string for edge functions.
  // This prevents a 1-hour display offset when the admin is in BST (UTC+1).
  const toUtcIsoString = (localDateTimeStr?: string): string | undefined =>
    localDateTimeStr ? new Date(localDateTimeStr).toISOString() : undefined;

  const getMinimumResellerExpiry = () => {
    const date = new Date();
    date.setSeconds(0, 0);
    date.setMinutes(date.getMinutes() + 1);
    return date;
  };

  const getMinimumResellerExpiryValue = () => formatDateTimeLocal(getMinimumResellerExpiry());

  const clampResellerExpiry = (value: string) => {
    if (!value) return getMinimumResellerExpiryValue();

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return getMinimumResellerExpiryValue();

    const minimum = getMinimumResellerExpiry();
    return parsed < minimum ? formatDateTimeLocal(minimum) : value;
  };

  const defaultResellerExpiresAt = () => {
    const date = getMinimumResellerExpiry();
    date.setHours(date.getHours() + 24);
    return formatDateTimeLocal(date);
  };

  const [resellerExpiresAt, setResellerExpiresAt] = useState(defaultResellerExpiresAt);
  // Only the setter is used: updating it re-renders the picker as the minimum moves.
  const [, setMinimumResellerExpiresAt] = useState(getMinimumResellerExpiryValue);

  // Debounce alternative email input (300ms) so validation doesn't run on every keystroke
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedAlternativeEmail(alternativeEmail);
    }, 300);
    return () => clearTimeout(handle);
  }, [alternativeEmail]);

  useEffect(() => {
    if (!magicLinkDialogOpen || linkDestination !== 'reseller_portal') return;

    const syncMinimumExpiry = () => {
      const nextMinimum = getMinimumResellerExpiryValue();
      setMinimumResellerExpiresAt(nextMinimum);
      setResellerExpiresAt((current) => clampResellerExpiry(current));
    };

    syncMinimumExpiry();
    const intervalId = window.setInterval(syncMinimumExpiry, 30000);

    return () => window.clearInterval(intervalId);
  }, [magicLinkDialogOpen, linkDestination]);

  // Each time the dialog opens: a frozen copy of the merchant (so autofill and
  // password managers cannot change it), fresh state and a new submission id.
  useEffect(() => {
    if (!open) return;
    setSelectedOrgForLink({ ...merchant });
    setEmailSent(false);
    setUseAlternativeEmail(false);
    setAlternativeEmail('');
    setDebouncedAlternativeEmail('');
    setGeneratedLink(null);
    setGeneratingLink(false);
    setCopiedLink(false);
    setMinimumResellerExpiresAt(getMinimumResellerExpiryValue());
    setResellerExpiresAt(defaultResellerExpiresAt());
    setLinkDestination(merchant.merchant_type === 'reseller' ? 'reseller_portal' : 'onboarding');
    setSubmissionIdentifier(generateSubmissionIdentifier());
    // Only on open: the merchant is captured once per opening on purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSendEmail = async () => {
    if (!selectedOrgForLink) {
      console.error('[Magic Link] No organization selected');
      toast({
        title: 'Error',
        description: 'No organization selected. Please close and try again.',
        variant: 'destructive',
      });
      return;
    }

    // Defensive validation - ensure the organization has a valid ID
    if (!selectedOrgForLink.id) {
      console.error('[Magic Link] Organization missing ID:', selectedOrgForLink);
      toast({
        title: 'Error',
        description: 'Invalid organization data. Please refresh and try again.',
        variant: 'destructive',
      });
      return;
    }
    const safeResellerExpiresAt = linkDestination === 'reseller_portal'
      ? clampResellerExpiry(resellerExpiresAt)
      : undefined;

    if (linkDestination === 'reseller_portal' && safeResellerExpiresAt !== resellerExpiresAt) {
      setMinimumResellerExpiresAt(getMinimumResellerExpiryValue());
      setResellerExpiresAt(safeResellerExpiresAt);
      toast({
        title: 'Expiry updated',
        description: 'Past times are not allowed. Please use the updated future time.',
      });
      return;
    }

    setIsSendingEmail(true);
    try {
      // Create company data object from the organization
      const companyData = {
        pocName: selectedOrgForLink.poc_name,
        pocEmail: selectedOrgForLink.poc_email,
        pocPhone: selectedOrgForLink.poc_phone || undefined,
        name: selectedOrgForLink.name,
        ein: selectedOrgForLink.ein,
        products: Array.isArray(selectedOrgForLink.products) ? (selectedOrgForLink.products as Product[]) : [],
        organization_id: selectedOrgForLink.id
      };

      const emailAddress = selectedOrgForLink.poc_email;

      const result = await sendMagicLinkEmail(
        emailAddress,
        alternativeEmail,
        companyData,
        false,
        linkDestination,
        toUtcIsoString(safeResellerExpiresAt)
      );

      if (result.success) {
        setEmailSent(true);
        toast({
          title: 'Email Sent',
          description: `Magic link email sent to ${alternativeEmail || emailAddress}`,
        });
      } else {
        console.error('[Magic Link] Failed to send email:', result.error);
        toast({
          title: 'Error',
          description: result.error || 'Failed to send magic link email',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('[Magic Link] Error sending magic link email:', error);
      toast({
        title: 'Error',
        description: 'Failed to send magic link email',
        variant: 'destructive',
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  return (
    <Dialog open={magicLinkDialogOpen} onOpenChange={setMagicLinkDialogOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Magic Link for {selectedOrgForLink?.name ?? merchant.name}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="flex items-center space-x-2 mb-4">
            <Checkbox
              id="use-alternative-email"
              checked={useAlternativeEmail}
              onCheckedChange={(checked) => setUseAlternativeEmail(!!checked)}
            />
            <Label htmlFor="use-alternative-email">Send to different email</Label>
          </div>

          {useAlternativeEmail && (
            <div className="space-y-2">
              <Label htmlFor="alternative-email">Alternative Email</Label>
              <Input
                id="alternative-email"
                type="email"
                value={alternativeEmail}
                onChange={(e) => setAlternativeEmail(e.target.value)}
                placeholder="Enter alternative email address"
                className="w-full"
                autoComplete="off"
              />
            </div>
          )}

          {selectedOrgForLink?.merchant_type === 'reseller' && (
            <div className="space-y-3 pt-2 border-t">
              <Label>Link Destination</Label>
              <RadioGroup
                value={linkDestination}
                onValueChange={(value) => setLinkDestination(value as LinkType)}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="onboarding" id="onboarding" />
                  <Label htmlFor="onboarding" className="font-normal">Onboarding Dashboard</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="reseller_portal" id="reseller_portal" />
                  <Label htmlFor="reseller_portal" className="font-normal">Reseller Portal (Purchase Orders)</Label>
                </div>
              </RadioGroup>
              {linkDestination === 'reseller_portal' && (() => {
                const parsedDate = resellerExpiresAt ? new Date(resellerExpiresAt) : new Date();
                const selectedDate = new Date(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate());
                const selectedHour = parsedDate.getHours();
                const selectedMinute = parsedDate.getMinutes();
                const now = new Date();
                const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                const isToday = selectedDate.getTime() === todayStart.getTime();
                const minHour = isToday ? now.getHours() : 0;
                const minMinute = isToday && selectedHour === now.getHours() ? now.getMinutes() + 1 : 0;

                const updateDateTime = (date: Date, hour: number, minute: number) => {
                  const d = new Date(date);
                  d.setHours(hour, minute, 0, 0);
                  setResellerExpiresAt(formatDateTimeLocal(d));
                };

                return (
                  <div className="space-y-2 pt-1">
                    <Label>Session expiry date & time</Label>
                    <div className="flex items-start gap-3">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className={cn("flex-1 justify-start text-left font-normal h-10", !resellerExpiresAt && "text-muted-foreground")}>
                            <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                            {resellerExpiresAt ? format(parsedDate, 'MMM d, yyyy') : 'Pick a date'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={selectedDate}
                            onSelect={(day) => {
                              if (!day) return;
                              const newIsToday = day.getTime() === todayStart.getTime();
                              let h = selectedHour;
                              let m = selectedMinute;
                              if (newIsToday) {
                                if (h < now.getHours()) { h = now.getHours(); m = now.getMinutes() + 1; }
                                else if (h === now.getHours() && m <= now.getMinutes()) { m = now.getMinutes() + 1; }
                                if (m >= 60) { h += 1; m = 0; }
                              }
                              updateDateTime(day, h, m);
                            }}
                            disabled={(date) => date < todayStart}
                            initialFocus
                            className={cn("p-3 pointer-events-auto")}
                          />
                        </PopoverContent>
                      </Popover>
                      <div className="flex items-center gap-1.5">
                        <Select
                          value={String(selectedHour)}
                          onValueChange={(v) => {
                            const h = parseInt(v);
                            let m = selectedMinute;
                            if (isToday && h === now.getHours() && m <= now.getMinutes()) { m = now.getMinutes() + 1; }
                            if (m >= 60) m = 0;
                            updateDateTime(selectedDate, h, m);
                          }}
                        >
                          <SelectTrigger className="w-[72px] h-10">
                            <SelectValue placeholder="HH" />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({ length: 24 }, (_, i) => i)
                              .filter((i) => !isToday || i >= minHour)
                              .map((i) => (
                                <SelectItem key={i} value={String(i)}>
                                  {String(i).padStart(2, '0')}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        <span className="text-muted-foreground font-medium">:</span>
                        <Select
                          value={String(selectedMinute)}
                          onValueChange={(v) => updateDateTime(selectedDate, selectedHour, parseInt(v))}
                        >
                          <SelectTrigger className="w-[72px] h-10">
                            <SelectValue placeholder="MM" />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({ length: 60 }, (_, i) => (
                              <SelectItem key={i} value={String(i)} disabled={isToday && selectedHour === now.getHours() && i < minMinute}>
                                {String(i).padStart(2, '0')}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {!Intl.DateTimeFormat().resolvedOptions().timeZone.includes('Los_Angeles') && resellerExpiresAt && (
                      <p className="text-xs text-muted-foreground">
                        {new Date(resellerExpiresAt).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', dateStyle: 'medium', timeStyle: 'short' })} Pacific
                      </p>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {(() => {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            const trimmedDebounced = (debouncedAlternativeEmail || '').trim();
            const altEmailInvalid = useAlternativeEmail && !emailRegex.test(trimmedDebounced);
            const isPending = useAlternativeEmail && alternativeEmail.trim() !== trimmedDebounced;
            const isDisabled = isSendingEmail || emailSent || altEmailInvalid || isPending || (linkDestination === 'reseller_portal' && clampResellerExpiry(resellerExpiresAt) !== resellerExpiresAt);
            const disabledReason = isPending
              ? 'Validating email…'
              : altEmailInvalid
                ? (!alternativeEmail ? 'Please enter an email address' : 'Please enter a valid email address')
                : null;

            const button = (
              <Button
                onClick={handleSendEmail}
                disabled={isDisabled}
                className="w-full"
              >
                {isSendingEmail ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : emailSent ? (
                  <>
                    <Check className="mr-2 h-4 w-4" />
                    Email Sent
                  </>
                ) : (
                  <>
                    <SendHorizontal className="mr-2 h-4 w-4" />
                    Send Magic Link {useAlternativeEmail ? `to ${alternativeEmail}` : ''}
                  </>
                )}
              </Button>
            );

            if (disabledReason) {
              return (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span tabIndex={0} className="block w-full">{button}</span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{disabledReason}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              );
            }
            return button;
          })()}

          <div className="text-sm text-gray-500 mt-4">
            <p>The magic link will be sent via email to {useAlternativeEmail ? alternativeEmail : selectedOrgForLink?.poc_email}.</p>
            <p className="mt-1">
              {linkDestination === 'reseller_portal'
                ? `The link allows access to the Purchase Orders dashboard. Session expires on ${new Date(resellerExpiresAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}.`
                : "The link allows access to edit this organization's information for 24 hours."}
            </p>
          </div>

          {/* Generate & Copy Link Section */}
          <div className="p-4 border border-dashed border-muted-foreground/30 rounded-lg bg-muted/20">
            <div className="flex items-center gap-2 mb-2">
              <Link className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium text-muted-foreground">Generate copyable link</span>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={async () => {
                if (!selectedOrgForLink) return;

                const safeResellerExpiresAt = linkDestination === 'reseller_portal'
                  ? clampResellerExpiry(resellerExpiresAt)
                  : undefined;

                if (linkDestination === 'reseller_portal' && safeResellerExpiresAt !== resellerExpiresAt) {
                  setMinimumResellerExpiresAt(getMinimumResellerExpiryValue());
                  setResellerExpiresAt(safeResellerExpiresAt);
                  toast({ title: 'Expiry updated', description: 'Past times are not allowed. Please use the updated future time.' });
                  return;
                }

                setGeneratingLink(true);
                setGeneratedLink(null);
                try {
                  const recipientEmail = useAlternativeEmail && alternativeEmail ? alternativeEmail : selectedOrgForLink.poc_email;
                  const primaryEmail = selectedOrgForLink.poc_email;
                  const callbackPath = linkDestination === 'reseller_portal' ? '/reseller/auth-callback' : '/auth-callback';
                  const recipientParam = recipientEmail !== primaryEmail ? `&recipient_email=${encodeURIComponent(recipientEmail)}` : '';
                  const redirectTo = `${window.location.origin}${callbackPath}?submission_identifier=${submissionIdentifier}${recipientParam}`;
                  const { data, error } = await invokeFunction(supabase, 'generate-magic-link', {
                    body: {
                      email: primaryEmail,
                      submissionIdentifier,
                      redirectTo,
                      organisationId: selectedOrgForLink.id,
                      linkType: linkDestination,
                      expiresAt: toUtcIsoString(safeResellerExpiresAt),
                      recipientEmail: recipientEmail !== primaryEmail ? recipientEmail : undefined
                    }
                  });
                  if (error || !data?.success) {
                    throw new Error(data?.error || error?.message || 'Failed to generate link');
                  }
                  setGeneratedLink(data.magicLink);
                  toast({ title: "Link generated", description: "Magic link is ready to copy." });
                } catch (err) {
                  const message = err instanceof Error ? err.message : '';
                  toast({ title: "Error", description: message || "Failed to generate link", variant: "destructive" });
                } finally {
                  setGeneratingLink(false);
                }
              }}
              disabled={generatingLink || isSendingEmail || (linkDestination === 'reseller_portal' && clampResellerExpiry(resellerExpiresAt) !== resellerExpiresAt)}
              className="w-full mb-2"
            >
              {generatingLink ? "Generating..." : "Generate Link"}
            </Button>
            {generatedLink && (
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={generatedLink}
                  className="text-xs font-mono"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(generatedLink);
                      setCopiedLink(true);
                      toast({ title: "Copied", description: "Magic link copied to clipboard." });
                      setTimeout(() => setCopiedLink(false), 2000);
                    } catch {
                      toast({ title: "Error", description: "Failed to copy link.", variant: "destructive" });
                    }
                  }}
                  title="Copy link"
                >
                  {copiedLink ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            )}
          </div>

        </div>

        <DialogFooter className="sm:justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => setMagicLinkDialogOpen(false)}
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MagicLinkDialog;
